import { useState, useEffect } from "react";
import styled from "styled-components";
import { Menu, X } from "lucide-react";
import { router } from "@inertiajs/react";
import { unmodule } from "../../../util";
import { useLandingLanguage } from "../../../contexts/landing-language";

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${(props) =>
    props.$scrolled ? "rgba(255, 255, 255, 0.98)" : "transparent"};
  backdrop-filter: ${(props) => (props.$scrolled ? "blur(12px)" : "none")};
  box-shadow: ${(props) =>
    props.$scrolled ? "0 2px 20px rgba(0, 0, 0, 0.08)" : "none"};
  z-index: 1000;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Nav = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
  }

  img {
    height: 50px;
    width: auto;
    display: block;
    transition: filter 0.4s ease;
  }

  @media (max-width: 768px) {
    img {
      height: 40px;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 48px;
  align-items: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: ${(props) => (props.$scrolled ? "#1a1a1a" : "#ffffff")};
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  letter-spacing: 1.5px;
  text-transform: uppercase;

  &:after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 0;
    height: 1px;
    background: ${(props) => (props.$scrolled ? "#1a1a1a" : "#ffffff")};
    transition: width 0.3s ease;
  }

  &:hover {
    opacity: 0.7;

    &:after {
      width: 100%;
    }
  }
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    gap: 8px;
  }
`;

const LangSwitcher = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => (props.$scrolled ? "rgba(26,26,26,0.25)" : "rgba(255,255,255,0.45)")};
  overflow: hidden;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const LangButton = styled.button`
  background: ${(props) =>
    props.$active
      ? props.$scrolled ? "#1a1a1a" : "rgba(255,255,255,0.9)"
      : "none"};
  color: ${(props) =>
    props.$active
      ? props.$scrolled ? "#ffffff" : "#1a1a1a"
      : props.$scrolled ? "#1a1a1a" : "#ffffff"};
  border: none;
  padding: 7px 13px;
  font-size: 11px;
  font-weight: 600;
  cursor: ${(props) => (props.$active ? "default" : "pointer")};
  letter-spacing: 1.5px;
  text-transform: uppercase;
  transition: all 0.25s ease;

  &:hover:not([disabled]) {
    background: ${(props) =>
      !props.$active && (props.$scrolled ? "rgba(26,26,26,0.08)" : "rgba(255,255,255,0.18)")};
  }
`;

const BookNowButton = styled.button`
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  padding: 14px 28px;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  flex-shrink: 0;

  &:hover {
    background: #333333;
    transform: translateY(-1px);
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${(props) => (props.$scrolled ? "#1a1a1a" : "#ffffff")};
  cursor: pointer;
  padding: 8px;
  transition: all 0.3s;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
  z-index: 998;
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: ${(props) => (props.$isOpen ? "0" : "-100%")};
  width: 320px;
  max-width: 85vw;
  height: 100vh;
  background: #ffffff;
  padding: 80px 40px 40px;
  transition: right 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
  overflow-y: auto;
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MobileNavLink = styled.a`
  color: #1a1a1a;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 20px 0;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    color: #c9a86c;
    padding-left: 8px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  color: #1a1a1a;
  cursor: pointer;
  padding: 8px;
  transition: all 0.3s;

  &:hover {
    opacity: 0.6;
  }
`;

const MobileBookButton = styled.button`
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  padding: 18px 28px;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  width: 100%;
  margin-top: 32px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  transition: all 0.3s;

  &:hover {
    background: #333333;
  }
`;

const MobileLangSwitcher = styled.div`
  display: flex;
  margin-top: 12px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const MobileLangButton = styled.button`
  flex: 1;
  background: ${(props) => (props.$active ? "#1a1a1a" : "none")};
  color: ${(props) => (props.$active ? "#ffffff" : "#1a1a1a")};
  border: none;
  padding: 14px 0;
  font-weight: 600;
  font-size: 12px;
  cursor: ${(props) => (props.$active ? "default" : "pointer")};
  letter-spacing: 1.5px;
  text-transform: uppercase;
  transition: all 0.25s;

  &:hover:not([disabled]) {
    background: ${(props) => (!props.$active ? "#f5f5f5" : "#1a1a1a")};
  }
`;

const Header = ({ forceScrolled = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolledState, setScrolledState] = useState(false);
  const { t, language, switchLocale } = useLandingLanguage();
  const scrolled = forceScrolled || scrolledState;

  useEffect(() => {
    if (forceScrolled) return;
    const handleScroll = () => {
      setScrolledState(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [forceScrolled]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    } else {
      const homeUrl = language === "id" ? `/id/hebe#${id}` : `/hebe#${id}`;
      router.visit(homeUrl);
    }
  };

  return (
    <>
      <HeaderContainer $scrolled={scrolled}>
        <Nav>
          <Logo onClick={() => scrollToSection("home")}>
            <img
              src={
                scrolled
                  ? unmodule(require("../../../assets/logo-dark.png"))
                  : unmodule(require("../../../assets/logo-light.png"))
              }
              alt="Hebe Hotel Logo"
            />
          </Logo>
          <NavLinks>
            <NavLink $scrolled={scrolled} onClick={() => scrollToSection("home")}>
              {t.nav.home}
            </NavLink>
            <NavLink $scrolled={scrolled} onClick={() => scrollToSection("about")}>
              {t.nav.about}
            </NavLink>
            <NavLink $scrolled={scrolled} onClick={() => scrollToSection("rooms")}>
              {t.nav.rooms}
            </NavLink>
            <NavLink $scrolled={scrolled} onClick={() => scrollToSection("facilities")}>
              {t.nav.facilities}
            </NavLink>
            <NavLink $scrolled={scrolled} onClick={() => scrollToSection("gallery")}>
              {t.nav.gallery}
            </NavLink>
            <NavLink $scrolled={scrolled} onClick={() => scrollToSection("contact")}>
              {t.nav.contact}
            </NavLink>
          </NavLinks>
          <RightActions>
            <LangSwitcher $scrolled={scrolled}>
              <LangButton $scrolled={scrolled} $active={language === "en"} onClick={() => switchLocale("en")}>
                EN
              </LangButton>
              <LangButton $scrolled={scrolled} $active={language === "id"} onClick={() => switchLocale("id")}>
                ID
              </LangButton>
            </LangSwitcher>
            <BookNowButton onClick={() => scrollToSection("booking")}>
              {t.nav.bookNow}
            </BookNowButton>
            <MobileMenuButton
              $scrolled={scrolled}
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </MobileMenuButton>
          </RightActions>
        </Nav>
      </HeaderContainer>

      <MobileMenuOverlay
        $isOpen={mobileMenuOpen}
        onClick={() => setMobileMenuOpen(false)}
      />
      <MobileMenu $isOpen={mobileMenuOpen}>
        <CloseButton onClick={() => setMobileMenuOpen(false)}>
          <X size={24} />
        </CloseButton>
        <MobileNavLinks>
          <MobileNavLink onClick={() => scrollToSection("home")}>
            {t.nav.home}
          </MobileNavLink>
          <MobileNavLink onClick={() => scrollToSection("about")}>
            {t.nav.about}
          </MobileNavLink>
          <MobileNavLink onClick={() => scrollToSection("rooms")}>
            {t.nav.rooms}
          </MobileNavLink>
          <MobileNavLink onClick={() => scrollToSection("facilities")}>
            {t.nav.facilities}
          </MobileNavLink>
          <MobileNavLink onClick={() => scrollToSection("gallery")}>
            {t.nav.gallery}
          </MobileNavLink>
          <MobileNavLink onClick={() => scrollToSection("contact")}>
            {t.nav.contact}
          </MobileNavLink>
        </MobileNavLinks>
        <MobileBookButton onClick={() => scrollToSection("booking")}>
          {t.nav.bookNow}
        </MobileBookButton>
        <MobileLangSwitcher>
          <MobileLangButton $active={language === "en"} onClick={() => switchLocale("en")}>
            EN
          </MobileLangButton>
          <MobileLangButton $active={language === "id"} onClick={() => switchLocale("id")}>
            ID
          </MobileLangButton>
        </MobileLangSwitcher>
      </MobileMenu>
    </>
  );
};

export default Header;
